import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useSubscription } from "./hooks/useSubscription";
import { SubscriptionPlan } from "@repo/types";
import { SubscriptionHeader } from "./components/subscription/SubscriptionHeader";
import { SubscriptionTableRow } from "./components/subscription/SubscriptionTableRow";
import { SubscriptionModal } from "./components/subscription/SubscriptionModal";
import { SubscriptionDetailsModal } from "./components/subscription/SubscriptionDetailsModal";

// ─── Main Page Component ──────────────────────────────────────────────────

const Subscription = () => {
  const {
    subscriptions,
    insertRequest,
    setInsertRequest,
    resetInsertForm,
    handleInsert,
    updateRequest,
    setUpdateRequest,
    handleUpdate,
    fetchSubscriptions,
    loading,
  } = useSubscription();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(
    null,
  );

  // Initial Data Fetch
  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const handleAddNew = () => {
    setIsEditMode(false);
    resetInsertForm();
    setIsModalOpen(true);
  };

  const handleViewDetails = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setIsDetailsOpen(true);
  };

  const handleEdit = (plan: SubscriptionPlan) => {
    setIsEditMode(true);
    setUpdateRequest({
      PlanID: plan.PlanID,
      PlanName: plan.PlanName,
      DurationMonths: plan.DurationMonths,
      Price: plan.Price,
      MaxUsers: (plan as any).MaxUsers || 0, // In case Get returns standard shape missing relations
      MaxRooms: (plan as any).MaxRooms || 0,
      Description: plan.Description,
      IsLifetime: plan.IsLifetime,
      IsActive: plan.IsActive,
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (isEditMode) {
      await handleUpdate();
    } else {
      await handleInsert();
    }
    setIsModalOpen(false);
  };

  return (
    <div className="space-y-6 animate-fadeIn pb-10">
      <SubscriptionHeader onAdd={handleAddNew} />

      {/* Plans List Table */}
      <div className="space-y-4">
        {loading && subscriptions.length === 0 ? (
          <div className="bg-white dark:bg-[#111c44] rounded-2xl p-16 shadow-sm border border-slate-200 dark:border-white/10 flex flex-col items-center justify-center gap-4">
            <Loader2
              className="animate-spin text-blue-600 dark:text-blue-400"
              size={32}
            />
            <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">
              Loading Subscription Plans...
            </p>
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="bg-white dark:bg-[#111c44] rounded-2xl p-16 shadow-sm border border-slate-200 dark:border-white/10 flex text-center flex-col items-center justify-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-slate-50 dark:bg-[#1b254b]/50 flex items-center justify-center text-slate-400 dark:text-slate-500 border border-slate-100 dark:border-white/10">
              <span className="text-2xl font-bold">📋</span>
            </div>
            <div className="space-y-1">
              <p className="text-lg font-bold text-slate-800 dark:text-slate-100">
                No plans available
              </p>
              <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">
                Click Add New Plan to create your first subscription package.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden xl:block bg-white dark:bg-[#111c44] rounded-2xl shadow-sm border border-slate-200 dark:border-white/10 overflow-hidden">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 dark:bg-[#1b254b]/50 border-b border-slate-200 dark:border-white/10">
                    <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Plan Name
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Pricing
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 dark:divide-white/5">
                  {subscriptions.map((plan, index) => (
                    <SubscriptionTableRow
                      key={plan.PlanID || `row-${index}`}
                      plan={plan}
                      onEdit={handleEdit}
                      onViewDetails={handleViewDetails}
                      view="table"
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile/Tablet Card View */}
            <div className="xl:hidden grid grid-cols-1 md:grid-cols-2 gap-4">
              {subscriptions.map((plan, index) => (
                <SubscriptionTableRow
                  key={plan.PlanID || `card-${index}`}
                  plan={plan}
                  onEdit={handleEdit}
                  onViewDetails={handleViewDetails}
                  view="card"
                />
              ))}
            </div>
          </>
        )}
      </div>

      <SubscriptionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isEditMode={isEditMode}
        loading={loading}
        formData={isEditMode ? updateRequest : insertRequest}
        setFormData={isEditMode ? setUpdateRequest : setInsertRequest}
        handleSave={handleSave}
      />

      <SubscriptionDetailsModal
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        plan={selectedPlan}
      />
    </div>
  );
};

export default Subscription;
